import type {
  EmployeeRow,
  IdentityDocument,
  Dependent,
  EmergencyContact,
  PinFields,
  PaymentMethod,
} from '../employeeTypes';
import { boolCode } from '../../../lib/boolCode';
import { mapLabeledCode, labeledCodeDisplay } from '../../../lib/labeledCode';
import { capitalizeFirst, capitalizeLabel } from '../../../lib/capitalize';
import type { SyncStatusType } from '../../../components/commons/syncStatus/SyncStatus.types';

type RawRow = Record<string, unknown>;

/**
 * Tri-state O/N decode: `'O'`/`1`/`true` → true, `'N'`/`0`/`false` → false, and
 * anything else → undefined (field absent). Built on the shared {@link boolCode}
 * so the O/N truth table lives in one place; the `?? false` at call sites decides
 * which fields collapse undefined to false.
 */
const yesNo = (v: unknown): boolean | undefined => {
  const code = boolCode(v);
  return code === undefined ? undefined : code === 'O';
};

/** Returns a trimmed non-empty string, or undefined. */
const str = (v: unknown): string | undefined => {
  const s = v != null ? String(v).trim() : '';
  return s !== '' ? s : undefined;
};

const mapDocument = (raw: RawRow): IdentityDocument => ({
  type: mapLabeledCode(raw.typeidentite),
  number: str(raw.numidentite),
  issue_date: str(raw.dateemission),
  issuing_place: str(raw.lieuemission),
  expiry_date: str(raw.dateexpiration),
  document_file: str(raw.fichieridentite),
});

const mapDependent = (raw: RawRow): Dependent => ({
  last_name: str(raw.nom),
  first_name: str(raw.prenom),
  birthdate: str(raw.datenaissance),
  birth_place: str(raw.birthplace),
  gender: mapLabeledCode(raw.sexe),
  nationality: capitalizeLabel(mapLabeledCode(raw.nationalite)),
  parent_name: str(raw.nompere),
  relationship: mapLabeledCode(raw.lien),
  dependent_status: mapLabeledCode(raw.statut),
  marital_status: capitalizeLabel(mapLabeledCode(raw.statutfamilial)),
  disability: mapLabeledCode(raw.handicap),
  custody: mapLabeledCode(raw.enfantacharge),
  working: mapLabeledCode(raw.conjointtravail),
  location: str(raw.location),
  document_type: str(raw.typedocument),
  document_validity_date: str(raw.validedate),
  document_file: str(raw.document),
  document_checked: yesNo(raw.documentchecked),
  is_active: yesNo(raw.actif),
  registration_date: str(raw.datechangeactif),
});

const mapPaymentMethod = (raw: RawRow): PaymentMethod => {
  const mode = capitalizeLabel(mapLabeledCode(raw.paymentmethod));
  return {
    id: str(raw.hc_uuid_key) ?? '',
    mode: mode?.libelle ?? mode?.code ?? '',
    operator: labeledCodeDisplay(raw.paymentorg) ?? '',
    accountNumber: str(raw.accountnumber) ?? str(raw.iban) ?? '',
    activePayslip: yesNo(raw.active_payslip) ?? false,
    activeAdvance: yesNo(raw.active_advance) ?? false,
    activeSettlement: yesNo(raw.active_settlement) ?? false,
  };
};

function mapPins(raw: RawRow): PinFields {
  const pins: Record<string, string | undefined> = {};
  for (let n = 1; n <= 13; n++) {
    const lc = mapLabeledCode(raw[`PIN${n}`]);
    if (!lc) continue;
    pins[`pin_${n}`] = lc.code;
    if (lc.libelle != null) pins[`pin_${n}_label`] = lc.libelle;
  }
  return pins as PinFields;
}

const mapEmergencyContact = (raw: RawRow): EmergencyContact => ({
  civility: mapLabeledCode(raw.civi),
  last_name: str(raw.nom),
  first_name: str(raw.prenom),
  phone: str(raw.tel),
  address: str(raw.adr),
  relationship: mapLabeledCode(raw.rel),
});

/**
 * Maps the raw backend detail response (DB column names, mixed types) to the
 * frontend EmployeeRow shape.
 *
 * Handles:
 * - Field renaming: nom→last_name, code_staff→file_number, civilite→civility, etc.
 * - Boolean coercion: "O"/"N" strings and 0/1 integers → true/false
 * - PIN casing: PIN1…PIN13 (uppercase) → pin_1…pin_13
 * - Nested arrays: identity_documents and dependents each get their own sub-mapper
 * - Absent fields default to undefined (or required-field defaults where needed)
 */
export function mapEmployeeDetailRow(raw: RawRow): EmployeeRow {
  return {
    // ── Required identifiers ──────────────────────────────────────────────────
    hc_uuid_key: String(raw.hc_uuid_key ?? ''),
    sync_status: (str(raw.sync_status) as SyncStatusType | undefined) ?? 'unknown',
    file_number: str(raw.code_staff) ?? '',
    first_name: capitalizeFirst(str(raw.prenom)) ?? '',
    last_name: str(raw.nom) ?? '',
    birthdate: str(raw.datenaissance) ?? '',
    entry_date: str(raw.dateentree) ?? '',
    field_code: str(raw.codeterrain) ?? '',
    origin_field: str(raw.origin_field_staff) ?? '',
    update_date: str(raw.hc_last_update) ?? '',
    is_active: yesNo(raw.statutactive) ?? false,

    // ── Identity ──────────────────────────────────────────────────────────────
    gender: mapLabeledCode(raw.sexe),
    marital_status: capitalizeLabel(mapLabeledCode(raw.statutfamilial)),

    // ── Location ──────────────────────────────────────────────────────────────
    country: capitalizeLabel(mapLabeledCode(raw.pays)),
    relocated_staff: mapLabeledCode(raw.relocatedstaff),

    // ── Employment dates ──────────────────────────────────────────────────────
    initial_start_date: str(raw.initialstartdate),
    contract_start_date: str(raw.datedeb),
    contract_end_date: str(raw.datefin),

    // ── Contract ──────────────────────────────────────────────────────────────
    contract_current: mapLabeledCode(raw.contratencours),
    contract_creation_type: mapLabeledCode(raw.typecreation),
    contract_type: mapLabeledCode(raw.typecontrat),
    contract_activity_rate: raw.tauxactivite != null ? Number(raw.tauxactivite) : undefined,
    contract_trial_period: mapLabeledCode(raw.periodeessai),

    // ── Position & remuneration ───────────────────────────────────────────────
    job_function: str(raw.fonction) as EmployeeRow['job_function'],
    contract_level: str(raw.niveau) as EmployeeRow['contract_level'],
    contract_salary_scale: str(raw.codegrillesalaire) as EmployeeRow['contract_salary_scale'],

    // ── Personal info ─────────────────────────────────────────────────────────
    civility: mapLabeledCode(raw.civilite),
    nationality: capitalizeLabel(mapLabeledCode(raw.nation)),
    birth_place: str(raw.lieunaissance),
    employee_card_number: str(raw.carteemploye),
    updated_by: str(raw.qui),
    staff_representative: mapLabeledCode(raw.delegue),
    social_security_number: str(raw.num_soc),

    // ── In case of death ──────────────────────────────────────────────────────
    // Backend wraps decede/causedeces in DicoRef ({ code, libelle }); the two are
    // consumed differently:
    // - decede: read the code and coerce O/N → boolean (mapLabeledCode also
    //   tolerates a plain-string fallback).
    // - cause_of_death: a display string, libelle-first with code as fallback
    //   (labeledCodeDisplay).
    // autrecausedeces and datedeces are plain scalar columns.
    deceased: yesNo(mapLabeledCode(raw.decede)?.code),
    cause_of_death: labeledCodeDisplay(raw.causedeces),
    other_cause_of_death: str(raw.autrecausedeces),
    date_of_death: str(raw.datedeces),
    // Beneficiary in case of death — plain scalar column (staff.adressecontact).
    death_beneficiary: str(raw.adressecontact),

    // ── PINs (backend sends PIN1…PIN13 as LabeledCode; libelle = terrain label) ─
    ...mapPins(raw),

    // ── Payment ───────────────────────────────────────────────────────────────
    payslip_by_email: yesNo(raw.envoibulletinmail),
    bqmodereglement: mapLabeledCode(raw.bqmodereglement),
    bqnom: str(raw.bqnom),
    bqnumerocompte: str(raw.bqnumerocompte),
    bqmodereglementact: mapLabeledCode(raw.bqmodereglementact),
    bqnomact: str(raw.bqnomact),
    bqnumerocompteact: str(raw.bqnumerocompteact),

    // ── Comment ───────────────────────────────────────────────────────────────
    comment: str(raw.commentaire),

    // ── Contact ───────────────────────────────────────────────────────────────
    home_phone: str(raw.tel_prive),
    office_phone: str(raw.tel_bureau),
    phone: str(raw.portable),
    fax: str(raw.fax),
    email: str(raw.email),
    email_pro: str(raw.emailpro),

    // ── Address ───────────────────────────────────────────────────────────────
    address: str(raw.adresse),

    // ── Emergency contacts (CTU_1 … CTU_2 from Homère Classic) ──────────────────
    emergency_contacts: [
      {
        civi: raw.civi_ctu_1,
        nom: raw.nom_ctu_1,
        prenom: raw.prenom_ctu_1,
        tel: raw.tel_ctu_1,
        adr: raw.adr_ctu_1,
        rel: raw.rel_ctu_1,
      },
      {
        civi: raw.civi_ctu_2,
        nom: raw.nom_ctu_2,
        prenom: raw.prenom_ctu_2,
        tel: raw.tel_ctu_2,
        adr: raw.adr_ctu_2,
        rel: raw.rel_ctu_2,
      },
    ]
      .filter(r => Object.values(r).some(v => str(v) !== undefined))
      .map(mapEmergencyContact),

    // ── Sub-documents ─────────────────────────────────────────────────────────
    identity_documents: Array.isArray(raw.identity_documents)
      ? (raw.identity_documents as RawRow[]).map(mapDocument)
      : undefined,
    dependents: Array.isArray(raw.dependents)
      ? (raw.dependents as RawRow[]).map(mapDependent)
      : undefined,
    payment_methods: Array.isArray(raw.payment_methods)
      ? (raw.payment_methods as RawRow[]).map(mapPaymentMethod)
      : undefined,
  };
}
